import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  pending:{ bg:'#FFF3DC', color:'#8B5E0A', label:'Pending' },
  approved:{ bg:'#E8F5EB', color:'#2E6B3A', label:'Approved' },
  rejected:{ bg:'#FDEAEA', color:'#8F2E24', label:'Rejected' },
  ready:{ bg:'#D3F9D8', color:'#2B8A3E', label:'Ready for Collection' },
  collected:{ bg:'#EDF3FF', color:'#3B5BDB', label:'Collected' },
  returned:{ bg:'#F9F8F5', color:'#8A8A8A', label:'Returned' },
};

export default function BrandResources() {
  const { user } = useAuth();
  const isBCO = user?.role === 'brand_content_officer';
  const canAction = ['super_admin','inventory_officer','production_team_lead','sales_manager'].includes(user?.role);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    purpose:'', required_datetime:'', routed_to:'inventory', notes:'',
    items:[{ description:'', quantity:1, notes:'' }]
  });

  useEffect(()=>{ load(); },[]);

  const load = () => {
    setLoading(true);
    get('/bc/resources').then(d=>setRequests(d.data||[])).finally(()=>setLoading(false));
  };

  const addItem = () => setForm(f=>({...f, items:[...f.items,{description:'',quantity:1,notes:''}]}));
  const removeItem = (i) => setForm(f=>({...f, items:f.items.filter((_,idx)=>idx!==i)}));
  const updateItem = (i, field, val) => setForm(f=>({
    ...f, items:f.items.map((item,idx)=>idx===i?{...item,[field]:val}:item)
  }));

  const save = async () => {
    if (!form.items[0]?.description) { alert('At least one item required'); return; }
    setSaving(true);
    try {
      await post('/bc/resources', form);
      setShowForm(false);
      setForm({purpose:'',required_datetime:'',routed_to:'inventory',notes:'',
        items:[{description:'',quantity:1,notes:''}]});
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status, notes='') => {
    try {
      await put('/bc/resources/'+id+'/status', { status, notes });
      load();
      setShowDetail(false);
    } catch(err) { alert(err.message); }
  };

  return (
    <div className="page">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',color:'#2C1F16',margin:'0 0 4px'}}>Resource Requests</h2>
          <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>Request products and props for content creation</p>
        </div>
        {(isBCO || user?.role==='super_admin') && (
          <button className="btn btn-primary btn-sm" onClick={()=>setShowForm(true)}>+ New Request</button>
        )}
      </div>

      {loading ? <div className="spinner"/> : requests.length === 0 ? (
        <div className="empty-state"><h3>No resource requests yet</h3></div>
      ) : (
        <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'#2C1F16'}}>
                {['Request #','Purpose','Route To','Items','Required By','Status',''].map(h=>(
                  <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                    color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em',fontWeight:700}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map((r,i)=>{
                const st = STATUS_COLORS[r.status]||STATUS_COLORS.pending;
                return (
                  <tr key={r.id} style={{background:i%2===0?'#fff':'#F9F8F5',cursor:'pointer'}}
                    onClick={()=>{ setSelected(r); setShowDetail(true); }}>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:'#A8893D'}}>{r.request_number}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#2C1F16'}}>{r.purpose||'—'}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555',textTransform:'capitalize'}}>{r.routed_to||'—'}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{r.item_count} item{r.item_count!==1?'s':''}</td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>
                      {r.required_datetime ? new Date(r.required_datetime).toLocaleDateString('en-NG',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}) : '—'}
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                      <span style={{fontSize:9,padding:'3px 10px',borderRadius:20,fontWeight:600,background:st.bg,color:st.color}}>{st.label}</span>
                    </td>
                    <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#A8893D'}}>→</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selected && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:500}}>
            <div className="modal-hdr">
              <span className="modal-title">Request {selected.request_number}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowDetail(false)}>X</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
                {[
                  ['Status', (STATUS_COLORS[selected.status]||STATUS_COLORS.pending).label],
                  ['Routed To', selected.routed_to||'—'],
                  ['Requested By', selected.requested_by_name||'—'],
                  ['Required By', selected.required_datetime ? new Date(selected.required_datetime).toLocaleDateString('en-NG') : '—'],
                ].map(([label,val])=>(
                  <div key={label} style={{background:'#F9F8F5',borderRadius:8,padding:10}}>
                    <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:3}}>{label}</div>
                    <div style={{fontSize:12,color:'#2C1F16',fontWeight:500}}>{val}</div>
                  </div>
                ))}
              </div>
              {selected.purpose && (
                <div style={{marginBottom:12,fontSize:12,color:'#555'}}>{selected.purpose}</div>
              )}
              {selected.notes && (
                <div style={{background:'#F9F8F5',borderRadius:8,padding:10,marginBottom:12,fontSize:12,color:'#555'}}>
                  {selected.notes}
                </div>
              )}
            </div>
            <div className="modal-ftr" style={{flexWrap:'wrap',gap:8}}>
              {canAction && selected.status==='pending' && (
                <>
                  <button className="btn btn-primary btn-sm"
                    onClick={()=>updateStatus(selected.id,'approved')}>Approve</button>
                  <button className="btn btn-ghost btn-sm" style={{color:'#8F2E24'}}
                    onClick={()=>updateStatus(selected.id,'rejected')}>Reject</button>
                </>
              )}
              {canAction && selected.status==='approved' && (
                <button className="btn btn-primary btn-sm"
                  onClick={()=>updateStatus(selected.id,'ready')}>Mark Ready</button>
              )}
              {selected.status==='ready' && (
                <button className="btn btn-primary btn-sm"
                  onClick={()=>updateStatus(selected.id,'collected')}>Mark Collected</button>
              )}
              {selected.status==='collected' && (
                <button className="btn btn-outline btn-sm"
                  onClick={()=>updateStatus(selected.id,'returned')}>Mark Returned</button>
              )}
              <button className="btn btn-outline" onClick={()=>setShowDetail(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* New Request Form */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:560}}>
            <div className="modal-hdr">
              <span className="modal-title">New Resource Request</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'75vh',overflowY:'auto'}}>
              <div className="form-row">
                <div className="form-group" style={{flex:2}}>
                  <label className="form-label">Purpose</label>
                  <input className="form-control" value={form.purpose}
                    onChange={e=>setForm({...form,purpose:e.target.value})}
                    placeholder="e.g. Resort Tote product shoot" />
                </div>
                <div className="form-group">
                  <label className="form-label">Route To</label>
                  <select className="form-control" value={form.routed_to}
                    onChange={e=>setForm({...form,routed_to:e.target.value})}>
                    <option value="inventory">Inventory</option>
                    <option value="production">Production</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Required Date & Time</label>
                  <input type="datetime-local" className="form-control"
                    value={form.required_datetime}
                    onChange={e=>setForm({...form,required_datetime:e.target.value})} />
                </div>
              </div>

              {/* Items */}
              <div style={{marginTop:12}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#556B2F',
                    textTransform:'uppercase',letterSpacing:'.08em'}}>
                    Items Requested
                  </div>
                  <button type="button" className="btn btn-outline btn-sm" onClick={addItem}>+ Add Item</button>
                </div>
                {form.items.map((item,i)=>(
                  <div key={i} style={{background:'#F9F8F5',borderRadius:8,padding:12,marginBottom:8}}>
                    <div className="form-row">
                      <div className="form-group" style={{flex:3}}>
                        <label className="form-label">Item Description *</label>
                        <input className="form-control" value={item.description}
                          onChange={e=>updateItem(i,'description',e.target.value)}
                          placeholder="e.g. Resort Tote bag (sample)" />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Qty</label>
                        <input type="number" className="form-control" min="1"
                          value={item.quantity}
                          onChange={e=>updateItem(i,'quantity',parseInt(e.target.value)||1)} />
                      </div>
                      {form.items.length > 1 && (
                        <div className="form-group" style={{display:'flex',alignItems:'flex-end'}}>
                          <button type="button" className="btn btn-ghost btn-sm"
                            style={{color:'#8F2E24'}} onClick={()=>removeItem(i)}>✕</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-group">
                <label className="form-label">Additional Notes</label>
                <textarea className="form-control" rows={2} value={form.notes}
                  onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving?'Submitting...':'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
